<template>
  <div>
    <!-- Hero Section -->
    <section class="py-20 bg-gradient-dark">
      <div class="container mx-auto px-6 text-center">
        <h1 class="text-5xl md:text-6xl font-display font-bold text-gold-400 mb-6">
          RX Forms
        </h1>
      </div>
    </section>

    <!-- Main Content Section -->
    <section class="py-20">
      <div class="container mx-auto px-6">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <!-- Download Cards Section -->
          <div class="space-y-8">
            <!-- Main RX Form Card -->
            <div class="bg-black-800 rounded-2xl p-8 border border-gold-800 transform transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-gold-900/30">
              <div class="flex items-start mb-6">
                <div class="w-16 h-16 bg-gradient-gold rounded-xl flex items-center justify-center mr-6 flex-shrink-0 transform transition-transform duration-500 hover:rotate-12 hover:scale-110">
                  <svg class="w-8 h-8 text-black-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 class="text-3xl font-display font-bold text-gold-400 mb-2">
                    RX Form
                  </h2>
                  <p class="text-gray-300">
                    Comprehensive form for all your dental restoration needs.
                  </p>
                </div>
              </div>
              
              <a :href="mainFormPath" 
                 download="CanadaSmile_Prescription_Form.pdf"
                 class="btn-primary w-full text-center inline-flex items-center justify-center text-lg py-4 transform transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-gold-500/20">
                <svg class="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                RX Form (PDF)
              </a>
            </div>
                        <div class="bg-gradient-dark rounded-2xl p-8 border border-gold-800 text-center">
              <h3 class="text-2xl font-display font-bold text-gold-400 mb-4">
                Need Help?
              </h3>
              <p class="text-gray-300 mb-6">
                Contact our team if you have any questions about the forms or submission process.
              </p>
              <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+14167279598" class="btn-primary">
                  <svg class="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Call (416) 727-9598
                </a>
                <a href="mailto:info@canadasmilelab.com" class="btn-secondary">
                  <svg class="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Support
                </a>
              </div>
            </div>
          </div>
          

          <!-- Instructions Section -->
          <div class="space-y-8">
            <!-- How to Use Section -->
            <div class="bg-black-800 rounded-2xl p-8 border border-gold-800">
              <h3 class="text-2xl font-display font-bold text-gold-400 mb-6">
                How to Submit Your Case
              </h3>
              
              <div class="space-y-6">
                <div v-for="(step, index) in submissionSteps" :key="index" 
                     class="flex items-start group">
                  <div class="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center mr-4 flex-shrink-0 transform transition-all duration-300 group-hover:scale-110">
                    <span class="text-black-900 font-bold text-xl">{{ index + 1 }}</span>
                  </div>
                  <div class="flex-1">
                    <h4 class="text-lg font-bold text-gold-300 mb-2 group-hover:text-gold-400 transition-colors duration-300">
                      {{ step.title }}
                    </h4>
                    <p class="text-gray-300">{{ step.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'RxForm',
  data() {
    return {
      mainFormPath: '/CanadaSmileLetters.pdf',
      formFeatures: [
        'Patient Information Section',
        'Doctor Information Section',
        'Case Type Selection',
        'Shade Specifications',
        'Material Selection Options',
        'Delivery Instructions',
        'Special Instructions Area',
        'Signature & Date Fields'
      ],
      additionalResources: [
        {
          title: 'Digital Scan Instructions',
          description: 'Guidelines for digital impressions',
          path: '/digital-scan-guide.pdf',
          filename: 'Digital_Scan_Guide.pdf'
        },
        {
          title: 'Shade Guide Reference',
          description: 'Color matching reference chart',
          path: '/shade-guide.pdf',
          filename: 'Shade_Guide_Reference.pdf'
        },
        {
          title: 'Shipping Labels',
          description: 'Pre-filled shipping labels',
          path: '/shipping-labels.pdf',
          filename: 'Shipping_Labels.pdf'
        }
      ],
      submissionSteps: [
        {
          title: 'Download & Print',
          description: 'Download the form and print it out.'
        },
        {
          title: 'Fill Out Completely',
          description: 'Complete all required fields with accurate patient and case information.'
        },
        {
          title: 'Submit Your Case',
          description: 'Email the completed form to info@canadasmilelab.com'
        }
      ],
      guidelines: [
        'Please use black ink and print clearly for best results.',
        'Include all relevant patient information and medical history.',
        'Specify shade requirements using our shade guide or provide a shade tab.',
        'Note any allergies or special considerations.',
        'Include your preferred contact method for case updates.',
        'For digital submissions, include scan files and any special instructions.'
      ]
    }
  },
  methods: {
    showDownloadToast(filename) {
      // Create a toast notification
      const toast = document.createElement('div')
      toast.className = 'fixed bottom-4 right-4 bg-gradient-to-r from-gold-600 to-gold-800 text-gray-900 px-6 py-3 rounded-lg shadow-lg z-50 animate-slide-in-right'
      toast.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Downloading ${filename}
        </div>
      `
      
      document.body.appendChild(toast)
      
      // Remove toast after 3 seconds
      setTimeout(() => {
        toast.classList.add('opacity-0', 'transition-opacity', 'duration-300')
        setTimeout(() => {
          if (toast.parentNode) {
            document.body.removeChild(toast)
          }
        }, 300)
      }, 3000)
    }
  },
  mounted() {
    // Add download event listeners
    const downloadLinks = document.querySelectorAll('a[download]')
    downloadLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const filename = link.getAttribute('download')
        this.showDownloadToast(filename)
      })
    })
  }
}
</script>

<style scoped>
/* Animation for slide in right */
@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in-right {
  animation: slideInRight 0.5s ease-out forwards;
}

/* Hover effects for cards */
.bg-black-800 {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.bg-black-800:hover {
  transform: translateY(-5px);
}
</style>